import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';

type ApplicationTreeState = {
  selectedNodes: string[];
  expandedNodes?: string[];
};

type DomainTreeState = {
  // Tree state scoped per application
  treeStateByApplication: Record<string, ApplicationTreeState>;

  // Actions
  getTreeState: (applicationId: string) => ApplicationTreeState;
  setSelectedNodes: (applicationId: string, nodes: string[]) => void;
  setExpandedNodes: (applicationId: string, nodes: string[]) => void;
  toggleNodeExpanded: (applicationId: string, nodeId: string) => void;
  expandNodes: (applicationId: string, nodeIds: string[]) => void;
  collapseNodes: (applicationId: string, nodeIds: string[]) => void;
  clearTreeState: (applicationId: string) => void;
  clearAllTreeState: () => void;
};

const DEFAULT_STATE: ApplicationTreeState = {
  selectedNodes: [],
};

export const useDomainTreeStore = create<DomainTreeState>()(
  persist(
    (set, get) => ({
      treeStateByApplication: {},

      getTreeState: (applicationId) => {
        const state = get().treeStateByApplication[applicationId];
        return state || DEFAULT_STATE;
      },

      setSelectedNodes: (applicationId, nodes) => {
        const { treeStateByApplication } = get();
        const currentState =
          treeStateByApplication[applicationId] || DEFAULT_STATE;
        set({
          treeStateByApplication: {
            ...treeStateByApplication,
            [applicationId]: {
              ...currentState,
              selectedNodes: nodes,
            },
          },
        });
      },

      setExpandedNodes: (applicationId, nodes) => {
        const { treeStateByApplication } = get();
        const currentState =
          treeStateByApplication[applicationId] || DEFAULT_STATE;
        set({
          treeStateByApplication: {
            ...treeStateByApplication,
            [applicationId]: {
              ...currentState,
              expandedNodes: nodes,
            },
          },
        });
      },

      toggleNodeExpanded: (applicationId, nodeId) => {
        const { treeStateByApplication } = get();
        const currentState =
          treeStateByApplication[applicationId] || DEFAULT_STATE;
        const currentExpandedNodes = currentState.expandedNodes ?? [];
        const expandedNodes = currentExpandedNodes.includes(nodeId)
          ? currentExpandedNodes.filter((id) => id !== nodeId)
          : [...currentExpandedNodes, nodeId];
        set({
          treeStateByApplication: {
            ...treeStateByApplication,
            [applicationId]: {
              ...currentState,
              expandedNodes,
            },
          },
        });
      },

      expandNodes: (applicationId, nodeIds) => {
        const { treeStateByApplication } = get();
        const currentState =
          treeStateByApplication[applicationId] || DEFAULT_STATE;
        const newExpanded = new Set([
          ...(currentState.expandedNodes ?? []),
          ...nodeIds,
        ]);
        set({
          treeStateByApplication: {
            ...treeStateByApplication,
            [applicationId]: {
              ...currentState,
              expandedNodes: Array.from(newExpanded),
            },
          },
        });
      },

      collapseNodes: (applicationId, nodeIds) => {
        const { treeStateByApplication } = get();
        const currentState =
          treeStateByApplication[applicationId] || DEFAULT_STATE;
        const toCollapse = new Set(nodeIds);
        set({
          treeStateByApplication: {
            ...treeStateByApplication,
            [applicationId]: {
              ...currentState,
              expandedNodes: (currentState.expandedNodes ?? []).filter(
                (id) => !toCollapse.has(id),
              ),
            },
          },
        });
      },

      clearTreeState: (applicationId) => {
        const { treeStateByApplication } = get();
        const { [applicationId]: _, ...rest } = treeStateByApplication;
        set({ treeStateByApplication: rest });
      },

      clearAllTreeState: () => set({ treeStateByApplication: {} }),
    }),
    {
      name: 'terra-proxy-domain-tree-state',
      storage: createJSONStorage(() => createTenantStorage()),
    },
  ),
);

// Convenience hooks that take applicationId
export const useTreeSelection = (applicationId: string | undefined) => {
  const treeState = useDomainTreeStore((state) =>
    applicationId ? state.treeStateByApplication[applicationId] : undefined,
  );
  const setSelectedNodes = useDomainTreeStore(
    (state) => state.setSelectedNodes,
  );

  const selectedNodes = treeState?.selectedNodes || [];

  return {
    selectedNodes,
    setSelectedNodes: (nodes: string[]) => {
      if (applicationId) {
        setSelectedNodes(applicationId, nodes);
      }
    },
  };
};

export const useTreeExpansion = (applicationId: string | undefined) => {
  const treeState = useDomainTreeStore((state) =>
    applicationId ? state.treeStateByApplication[applicationId] : undefined,
  );
  const setExpandedNodes = useDomainTreeStore(
    (state) => state.setExpandedNodes,
  );
  const toggleNodeExpanded = useDomainTreeStore(
    (state) => state.toggleNodeExpanded,
  );
  const expandNodes = useDomainTreeStore((state) => state.expandNodes);
  const collapseNodes = useDomainTreeStore((state) => state.collapseNodes);

  const expandedNodes = treeState?.expandedNodes;

  return {
    expandedNodes,
    setExpandedNodes: (nodes: string[]) => {
      if (applicationId) {
        setExpandedNodes(applicationId, nodes);
      }
    },
    toggleNodeExpanded: (nodeId: string) => {
      if (applicationId) {
        toggleNodeExpanded(applicationId, nodeId);
      }
    },
    expandNodes: (nodeIds: string[]) => {
      if (applicationId) {
        expandNodes(applicationId, nodeIds);
      }
    },
    collapseNodes: (nodeIds: string[]) => {
      if (applicationId) {
        collapseNodes(applicationId, nodeIds);
      }
    },
  };
};
